import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
} from '@groundworks/ngx-groundworks-ui';
import { PaymentRequestService } from '../../services/payment-request.service';

@Component({
  selector: 'app-add-contact-modal',
  imports: [ReactiveFormsModule, ModalComponent, ModalBodyComponent, ModalFooterComponent],
  templateUrl: './add-contact-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddContactModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly contacts = inject(PaymentRequestService);
  private readonly fb = inject(FormBuilder);

  readonly title = 'Add a New Contact';
  readonly opportunityId = signal<string | null>(null);
  private readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    email: ['', Validators.email],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  readonly canSave = computed(() => this.formStatus() === 'VALID' && !this.saving());

  readonly footerActions = computed<ModalFooterAction[]>(() => [
    { label: 'Cancel', type: 'secondary', action: () => this.cancel() },
    { label: 'Save New Contact', type: 'primary', action: () => this.save(), disabled: () => !this.canSave() },
  ]);

  save(): void {
    const opportunityId = this.opportunityId();
    if (opportunityId == null || !this.canSave()) return;
    const v = this.form.getRawValue();
    this.saving.set(true);
    this.errorMessage.set(null);
    this.contacts
      .createOpportunityContact(opportunityId, {
        firstName: v.firstName,
        lastName: v.lastName,
        phoneNumber: v.phoneNumber,
        email: v.email.trim() === '' ? null : v.email,
      })
      .subscribe({
        next: (created) => {
          this.saving.set(false);
          this.activeModal.close(created);
        },
        error: (err) => {
          this.saving.set(false);
          this.errorMessage.set(detailFromError(err) ?? 'Could not save the contact. Please try again.');
        },
      });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}

function detailFromError(err: unknown): string | null {
  const e = err as { detail?: unknown; error?: { detail?: unknown } } | null;
  const detail = e?.detail ?? e?.error?.detail;
  return typeof detail === 'string' ? detail : null;
}
